import Card from '@mui/material/Card';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { useTheme } from '@mui/material/styles';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebaseConfig';

function PayoutTable({ payouts, isAdmin, paypalEmail }) {
  const theme = useTheme();

  const handleUpdateStatus = async (payoutId, newStatus, amount, earningsCleared) => {
    try {
      if (payoutId.startsWith('payout')) {
        console.log('Mock data: Status update simulation for ID:', payoutId);
        return;
      }

      const updateData = {
        status: newStatus,
        processedAt: new Date(),
        processedBy: localStorage.getItem('userID'),
      };

      if (newStatus === 'paid') {
        updateData.earningsCleared = earningsCleared || amount;
      }

      await updateDoc(doc(db, 'payoutRequests', payoutId), updateData);
      console.log(`Payout ${payoutId} updated to status: ${newStatus}`);
    } catch (error) {
      console.error('Error updating payout status:', error);
    }
  };

  const colCount = isAdmin ? 5 : 4;
  const columnTemplate = isAdmin
    ? '20% 20% 20% 20% 20%'
    : '25% 25% 25% 25%';

  const headerCellStyles = {
    padding: '16px',
    borderBottom: `2px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.background.default || '#bac7db',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  };

  const cellStyles = {
    padding: '16px',
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'denied':
        return 'error';
      default:
        return 'text';
    }
  };

  return (
    <Card
      sx={{
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: 'white',
        width: '100%',
      }}
    >
      <MDBox p={3}>
        <MDTypography variant="h6" mb={3}>
          Payout History {isAdmin && '(Admin View)'}
        </MDTypography>

        {!payouts || payouts.length === 0 ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <MDTypography variant="button" color="text">
              No payout history available
            </MDTypography>
          </MDBox>
        ) : (
          <>
            {/* Static Header Row */}
            <MDBox
              display="grid"
              gridTemplateColumns={columnTemplate}
              sx={{ borderBottom: `2px solid ${theme.palette.grey[300]}` }}
            >
              <MDBox sx={headerCellStyles}>
                <MDTypography variant="h6" component="span" fontWeight="bold">
                  Date
                </MDTypography>
              </MDBox>
              <MDBox sx={headerCellStyles}>
                <MDTypography variant="h6" component="span" fontWeight="bold">
                  Amount
                </MDTypography>
              </MDBox>
              <MDBox sx={headerCellStyles}>
                <MDTypography variant="h6" component="span" fontWeight="bold">
                  Status
                </MDTypography>
              </MDBox>
              <MDBox sx={headerCellStyles}>
                <MDTypography variant="h6" component="span" fontWeight="bold">
                  PayPal Email
                </MDTypography>
              </MDBox>
              {isAdmin && (
                <MDBox sx={headerCellStyles}>
                  <MDTypography variant="h6" component="span" fontWeight="bold">
                    Actions
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>

            {/* Body Rows */}
{[...payouts]
  .sort((a, b) => {
    // Prefer explicit year/month (your payout docs store these)
    if (Number.isFinite(a.year) && Number.isFinite(b.year) && Number.isFinite(a.month) && Number.isFinite(b.month)) {
      if (a.year !== b.year) return b.year - a.year;
      if (a.month !== b.month) return b.month - a.month;
    }

    // Fallback to requestedAt date
    const toMs = (p) => {
      if (p?.requestedAt?.seconds) return p.requestedAt.seconds * 1000;
      const d = new Date(p?.requestedAt);
      return Number.isFinite(d.getTime()) ? d.getTime() : 0;
    };

    return toMs(b) - toMs(a);
  })
  .map((payout) => (
              <MDBox
                key={payout.id}
                display="grid"
                gridTemplateColumns={columnTemplate}
                alignItems="center"
              >
                <MDBox sx={cellStyles}>
                  <MDTypography variant="button" color="text">
                    {new Date(
                      payout.requestedAt?.seconds * 1000 || payout.requestedAt
                    ).toLocaleDateString()}
                  </MDTypography>
                </MDBox>
                <MDBox sx={cellStyles}>
                  <MDTypography variant="button" fontWeight="medium" color="info">
                    ${payout.amount.toFixed(2)}
                  </MDTypography>
                </MDBox>
                <MDBox sx={cellStyles}>
                  <MDTypography variant="button" color={getStatusColor(payout.status)}>
                    {payout.status}
                  </MDTypography>
                </MDBox>
                <MDBox sx={cellStyles}>
                  <MDTypography variant="button" color="text">
                    {payout.paypalEmail || paypalEmail || 'N/A'}
                  </MDTypography>
                </MDBox>
                {isAdmin && (
                  <MDBox sx={cellStyles}>
                    {payout.status === 'pending' && (
                      <MDBox display="flex" gap={1}>
                        <MDButton
                          color="success"
                          size="small"
                          onClick={() =>
                            handleUpdateStatus(
                              payout.id,
                              'paid',
                              payout.amount,
                              payout.earningsCleared
                            )
                          }
                        >
                          Mark Paid
                        </MDButton>
                        <MDButton
                          color="error"
                          size="small"
                          onClick={() =>
                            handleUpdateStatus(payout.id, 'denied', payout.amount, 0)
                          }
                        >
                          Deny
                        </MDButton>
                      </MDBox>
                    )}
                  </MDBox>
                )}
              </MDBox>
            ))}
          </>
        )}
      </MDBox>
    </Card>
  );
}

export default PayoutTable;
